<script setup>
import {computed, onMounted, ref} from "vue";

const emit = defineEmits(['chapter-completion-update'])

const props = defineProps({
    courseId: {
        type: Number,
        required: true
    },
    currentChapterId: {
        type: Number,
        required: false,
        default: null,
    }
})

const course = ref(null)
const chapterCompletion = ref(null)
const open = ref()

const loadChapters = async () => {
    const res = await axios.get(route('api.course.customerarea.chapters', props.courseId))

    course.value = res.data.course
    chapterCompletion.value = res.data.chapter_completions
}

const updateCompletion = async (chapter) => {
    await axios.post(route('api.course.customerarea.chapter.toggle-completed', [props.courseId, chapter.id]))
    emit('chapter-completion-update', chapter)
}

onMounted(() => {
    loadChapters()
})

</script>
<template>
    <div
        class="lg:w-72 lg:shrink-0"
    >
        <span
            class="lg:hidden fixed bottom-4 right-4 z-modal-toggler"
        >
            <button-secondary
                @click="open = !open"
            >
                <span class="block relative">
                    <span :class="open ? 'opacity-0' : ''">{{ _mft('course:showChapters') }}</span>
                    <span
                        v-if="open"
                        class="block bg-white absolute inset-0 rounded-wl"
                    >
                        {{ _mft('shared:modal.close')}}
                    </span>
                </span>
            </button-secondary>
        </span>
        <div
            class="fixed top-16 bottom-0 z-modal-backdrop bg-black transition-opacity lg:hidden"
            :class="open ? 'left-0 w-screen opacity-10' : 'left-full opacity-0'"
            @click="open = false"
        >
        </div>
        <div
            class="w-4/5 md:w-72 fixed top-16 bottom-0 transition-all z-modal lg:z-auto max-h-screen overflow-y-scroll pb-24"
            :class="open ? 'right-0' : '-right-full lg:left-0 lg:right-auto'"
        >
            <div
                v-if="course"
                class="bg-white border-r py-4 rounded-wl shadow-xl mt-4 mx-1"
            >
                <div class="px-1 mb-4 text-center">
                    <h4 class="font-bold">
                        <a :href="route('course.customerarea.show', course.id)" class="text-inherit text-sm mf-link-immune">
                            <span class="text-md">{{ course.title }}</span>
                        </a>
                    </h4>
                </div>
                <ul class="px-1 space-y-2">
                    <li
                        v-for="(chapter, i) in course.chapters"
                        :key="chapter.id"
                        class="flex items-start justify-between relative hover:bg-gray-50 leading-5 bg-white rounded-wl border overflow-hidden"
                        :class="chapter.published && (chapter.available || course.chapters[i - 1]?.completed) ? chapter.id === currentChapterId ? 'border-gray-400 shadow-md' : 'border-gray-300' : 'border-gray-100'"
                    >
                        <div class="flex items-stretch justify-start">
                            <img
                                v-if="chapter.thumbnail_url"
                                :src="chapter.thumbnail_url"
                                class="w-16 aspect-video shrink-0 object-cover"
                                :class="chapter.published && (chapter.available || course.chapters[i - 1]?.completed) ? '' : 'opacity-50'"
                                alt=""
                            />
                            <div class="p-2">
                                <a
                                    v-if="chapter.published && (chapter.available || course.chapters[i - 1]?.completed)"
                                    :href="route('course.customerarea.chapter.show', [course.id, chapter.id])"
                                    class="text-inherit text-xs mf-link-immune"
                                >
                                    <div>
                                        <div :class="chapter.id === currentChapterId ? 'font-bold' : ''">{{ chapter.title }}</div>
                                        <div
                                            v-if="chapter.description"
                                            class="w-40 overflow-hidden text-ellipsis whitespace-nowrap text-gray-500 text-xxs"
                                        >
                                            {{ chapter.description }}
                                        </div>
                                    </div>
                                </a>
                                <div
                                    v-else
                                    class="text-gray-400 cursor-not-allowed text-xs"
                                >
                                    <div>
                                        {{ chapter.title }}
                                    </div>
                                    <div
                                        v-if="chapter.description"
                                        class="w-40 overflow-hidden text-ellipsis whitespace-nowrap text-gray-500 text-xxs"
                                    >
                                        {{ chapter.description }}
                                    </div>
                                </div>
                                <div
                                    v-if="course.has_multiple_providers && chapter.provider"
                                    class="text-gray-400 text-xxs"
                                >{{ _mft('shared:by') }} {{ chapter.provider.name }}</div>
                            </div>
                        </div>
                        <div
                            v-if="chapter.available || course.chapters[i - 1]?.completed"
                            class="pr-2 pt-1"
                        >
                            <toggle-done
                                v-model:content="chapter.completed"
                                @update:content="updateCompletion(chapter)"
                                :disabled="! chapter.published"
                                class="w-4 h-4 rounded-sm ml-1 shrink-0 mt-1"
                            >Toggle</toggle-done>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
